<script setup lang="ts">
import axios from 'axios';
import { onUnmounted, reactive, ref } from 'vue';
import { armarUrl } from '@idem/common/armar-url';
import { useRouter } from 'vue-router';

import ModalCropper from './components/modal-cropper.vue';
import ActividadEditable from './components/actividad-editable.vue';
import { asignarDireccion } from './utils/direccion.utils';

const apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
const actividadesPendientes = ref<any[]>([]);
const actividadesReprobadas = ref<any[]>([]);
const actividadesPublicadas = ref<any[]>([]);
type ActividadDraft = {
  nombre: string;
  descripcion: string;
  fecha: string;
  hora: string;
  direccion: string;
  latitud: string | number;
  longitud: string | number;
  link: string;
  macrocategoria: string;
  foto?: File;
  fotoPreview?: string;
};

type EditState = {
  draft: ActividadDraft;
};

const editStates = reactive<Record<string, EditState>>({});
const savingActivities = reactive<Record<string, boolean>>({});
const actividadCropper = ref<string>();
const fotoParaRecortar = ref<File>();
const router = useRouter();

async function cargarActividades() {
  try {
    const [pendientesResponse, reprobadasResponse, publicadasResponse] = await Promise.all([
      axios.get(`${apiBaseUrl}/super/actividades/por-visar`),
      axios.get(`${apiBaseUrl}/super/actividades/reprobadas`),
      axios.get(`${apiBaseUrl}/super/actividades/publicadas`),
    ]);
    actividadesPendientes.value = pendientesResponse.data;
    actividadesReprobadas.value = reprobadasResponse.data;
    actividadesPublicadas.value = publicadasResponse.data;
  } catch (e: any) {
    if (e.status === 401) {
      await router.push('/login');
    }
  }
}

await cargarActividades();

function limpiarEditState(id: string) {
  const preview = editStates[id]?.draft.fotoPreview;
  if (preview) URL.revokeObjectURL(preview);
  if (actividadCropper.value === id) {
    actividadCropper.value = undefined;
    fotoParaRecortar.value = undefined;
  }
  delete editStates[id];
}

async function gestionar(id: string, accion: 'aprobar' | 'reprobar' | 'eliminar') {
  if (accion === 'eliminar') {
    if (!window.confirm('¿Eliminar esta actividad?')) {
      return;
    }
    await axios.delete(`${apiBaseUrl}/super/actividades/${id}`);
    await cargarActividades();
    return;
  }
  await axios.put(`${apiBaseUrl}/super/actividades/${id}/${accion}`);
  limpiarEditState(id);
  await cargarActividades();
}

function numericCoordinate(value: string | number): number | undefined {
  if (value === '') return undefined;
  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : undefined;
}

async function uploadFile(file: File, idActividad: string) {
  const formData = new FormData();
  formData.append('foto', file);
  await axios.post(`${apiBaseUrl}/actividades/${idActividad}/foto`, formData);
}

async function guardarCambios(id: string) {
  const editState = editStates[id];
  if (!editState || savingActivities[id]) return;

  savingActivities[id] = true;
  try {
    const { draft } = editState;
    const payload = {
      id,
      nombre: draft.nombre,
      descripcion: draft.descripcion,
      fecha: draft.fecha,
      hora: draft.hora,
      direccion: draft.direccion,
      latitud: numericCoordinate(draft.latitud),
      longitud: numericCoordinate(draft.longitud),
      link: draft.link,
      macrocategoria: draft.macrocategoria || undefined,
    };
    const response = await axios.put(`${apiBaseUrl}/actividades`, payload);
    const idActividad = response.data || id;
    if (draft.foto) await uploadFile(draft.foto, idActividad);

    limpiarEditState(id);
    await cargarActividades();
    alert('Actividad guardada correctamente');
  } catch (e: any) {
    alert(e?.response?.data?.message || 'No se pudo guardar la actividad');
  } finally {
    delete savingActivities[id];
  }
}

function toggleEdit(actividad: any) {
  if (editStates[actividad.id]) {
    limpiarEditState(actividad.id);
    return;
  }

  editStates[actividad.id] = {
    draft: {
      nombre: actividad.nombre || '',
      descripcion: actividad.descripcion || '',
      fecha: actividad.fecha || '',
      hora: actividad.hora || '',
      direccion: actividad.direccion || actividad.direccionActividad || '',
      latitud: actividad.latitud || '',
      longitud: actividad.longitud || '',
      link: actividad.link || '',
      macrocategoria: actividad.macrocategoria || '',
    },
  };
}

function onAddressSelected(id: string, direccion: any) {
  const editState = editStates[id];
  if (editState) asignarDireccion(direccion, editState.draft);
}

function iniciarCropper(id: string, foto: File) {
  actividadCropper.value = id;
  fotoParaRecortar.value = foto;
}

function onPhotoSelected(id: string, event: Event) {
  const archivo = (event.target as HTMLInputElement).files?.[0];
  if (!archivo) return;
  if (archivo.type !== 'image/jpeg' && archivo.type !== 'image/png') {
    alert('Solo se permiten archivos JPEG y PNG');
    return;
  }
  iniciarCropper(id, archivo);
}

async function cropExistingPhoto(actividad: any) {
  const draft = editStates[actividad.id]?.draft;
  const source = draft?.foto || actividad.foto;
  if (!source) return;
  try {
    const blob = source instanceof File ? source : await (await fetch(armarUrl(source))).blob();
    iniciarCropper(actividad.id, new File([blob], 'foto.jpg', { type: blob.type || 'image/jpeg' }));
  } catch {
    alert('No se pudo cargar la foto para recortarla');
  }
}

function onCropConfirmed(archivo: File) {
  const id = actividadCropper.value;
  if (!id || !editStates[id]) return;
  const draft = editStates[id].draft;
  if (draft.fotoPreview) URL.revokeObjectURL(draft.fotoPreview);
  draft.foto = archivo;
  draft.fotoPreview = URL.createObjectURL(archivo);
  actividadCropper.value = undefined;
  fotoParaRecortar.value = undefined;
}

onUnmounted(() => {
  Object.values(editStates).forEach(({ draft }) => {
    if (draft.fotoPreview) URL.revokeObjectURL(draft.fotoPreview);
  });
});
</script>

<template>
  <section class="mx-auto max-w-6xl px-4 py-8">
    <h1 class="mb-6 text-3xl font-bold">Actividades por Visar</h1>
    <actividad-editable
      v-for="actividad in actividadesPendientes"
      :key="actividad.id"
      :actividad="actividad"
      v-model:edit-state="editStates[actividad.id]"
      :saving="savingActivities[actividad.id]"
      :permitir-gestion="true"
      @toggle-edit="toggleEdit(actividad)"
      @save="guardarCambios(actividad.id)"
      @manage="accion => gestionar(actividad.id, accion)"
      @address-selected="direccion => onAddressSelected(actividad.id, direccion)"
      @photo-selected="event => onPhotoSelected(actividad.id, event)"
      @crop-existing-photo="cropExistingPhoto(actividad)"
    />

    <h1 class="my-6 text-3xl font-bold">Actividades Reprobadas</h1>
    <actividad-editable
      v-for="actividad in actividadesReprobadas"
      :key="actividad.id"
      :actividad="actividad"
      v-model:edit-state="editStates[actividad.id]"
      :saving="savingActivities[actividad.id]"
      :permitir-gestion="false"
      @toggle-edit="toggleEdit(actividad)"
      @save="guardarCambios(actividad.id)"
      @address-selected="direccion => onAddressSelected(actividad.id, direccion)"
      @photo-selected="event => onPhotoSelected(actividad.id, event)"
      @crop-existing-photo="cropExistingPhoto(actividad)"
    />

    <h1 class="my-6 text-3xl font-bold">Actividades Publicadas</h1>
    <actividad-editable
      v-for="actividad in actividadesPublicadas"
      :key="actividad.id"
      :actividad="actividad"
      v-model:edit-state="editStates[actividad.id]"
      :saving="savingActivities[actividad.id]"
      :permitir-gestion="true"
      @toggle-edit="toggleEdit(actividad)"
      @save="guardarCambios(actividad.id)"
      @manage="accion => gestionar(actividad.id, accion)"
      @address-selected="direccion => onAddressSelected(actividad.id, direccion)"
      @photo-selected="event => onPhotoSelected(actividad.id, event)"
      @crop-existing-photo="cropExistingPhoto(actividad)"
    />
    <modal-cropper
      v-if="actividadCropper && fotoParaRecortar"
      :foto="fotoParaRecortar"
      @confirm="onCropConfirmed"
    />
  </section>
</template>
